import { useEffect, useRef, useState } from "react";
import { Icon } from "./icons";
import { facultyShort, formShort } from "./labels";

/** A group the student opened before; kept per device. */
export interface RecentGroup {
  group: string;
  /** Catalog file id, or the saved schedule id for an imported Word file. */
  document: string;
  fileId?: string;
  faculty?: string;
  course?: string;
}
interface GroupResult {
  group: string;
  fileId: string;
  file: string;
  faculty: string;
  course: string;
  form: string;
  period: string;
}
interface Facet {
  name: string;
  count: number;
}
interface GroupsResponse {
  results: GroupResult[];
  facets: { faculties: Facet[]; courses: Facet[]; forms: Facet[] };
  total: number;
  year: string;
}

const PLAIN_PERIOD = /^(осенний|весенний) семестр$|^расписание из word$/i;
const describe = (r: { faculty?: string; course?: string; form?: string; period?: string }) =>
  [
    r.faculty && facultyShort(r.faculty),
    r.course,
    r.form && formShort(r.form),
    r.period && !PLAIN_PERIOD.test(r.period) ? r.period.toLowerCase() : "",
  ]
    .filter(Boolean)
    .join(" · ");

export default function Groups({
  current,
  siblings,
  subgroups,
  subgroup,
  onSubgroup,
  onSibling,
  recent,
  onRecent,
  onOpen,
  onImport,
  online,
  initialQuery,
  firstRun,
}: {
  current: { group: string; faculty?: string; course?: string; name: string } | null;
  siblings: string[];
  subgroups: string[];
  subgroup: string | null;
  onSubgroup: (label: string | null) => void;
  onSibling: (group: string) => void;
  recent: RecentGroup[];
  onRecent: (item: RecentGroup) => void;
  onOpen: (fileId: string, group: string) => Promise<boolean>;
  onImport: () => void;
  online: boolean;
  initialQuery: string;
  firstRun: boolean;
}) {
  const [query, setQuery] = useState(initialQuery),
    [faculty, setFaculty] = useState(current?.faculty || ""),
    [course, setCourse] = useState(""),
    [form, setForm] = useState(""),
    [data, setData] = useState<GroupsResponse | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [opening, setOpening] = useState("");
  const request = useRef(0),
    input = useRef<HTMLInputElement>(null);

  useEffect(() => setQuery(initialQuery), [initialQuery]);
  useEffect(() => {
    if (firstRun) input.current?.focus();
  }, [firstRun]);
  useEffect(() => {
    if (!online) return;
    const id = ++request.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query.trim(), faculty, course, form });
        const r = await fetch(`/api/groups?${params}`);
        const body = await r.json();
        if (!r.ok) throw new Error(body.error || "Не удалось загрузить группы.");
        if (id === request.current) {
          setData(body);
          setError("");
        }
      } catch (e) {
        if (id === request.current)
          setError(
            e instanceof TypeError
              ? "Сервер недоступен. Откройте группу из недавних или попробуйте позже."
              : (e as Error).message,
          );
      } finally {
        if (id === request.current) setLoading(false);
      }
    }, query ? 200 : 0);
    return () => window.clearTimeout(timer);
  }, [query, faculty, course, form, online]);

  const searching = query.trim().length >= 2;
  const open = async (r: GroupResult) => {
    const key = `${r.fileId}|${r.group}`;
    setOpening(key);
    await onOpen(r.fileId, r.group);
    setOpening("");
  };
  const results = data?.results || [];
  const others = recent;

  return (
    <section className="groups-screen" aria-label="Выбор группы">
      {firstRun && (
        <div className="groups-hero">
          <h2>Найдите свою группу</h2>
          <p>Расписание откроется сразу и будет доступно без интернета.</p>
        </div>
      )}
      <label className="search-bar">
        <Icon name="search" />
        <input
          ref={input}
          type="search"
          inputMode="search"
          autoComplete="off"
          placeholder="Номер группы, например 23-БА"
          aria-label="Номер группы"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button
            className="icon-button"
            aria-label="Очистить поиск"
            onClick={() => setQuery("")}
          >
            <Icon name="close" />
          </button>
        )}
      </label>

      {error && (
        <div role="status" className="status-message error">
          <Icon name="error" />
          <span>{error}</span>
        </div>
      )}
      {!online && (
        <div role="status" className="status-message">
          <Icon name="cloud_off" />
          <span>
            Нет сети: поиск по каталогу недоступен. Сохранённые группы открываются
            и без интернета.
          </span>
        </div>
      )}

      {searching ? (
        <div className="result-group" aria-live="polite">
          <h3>
            {loading && !results.length
              ? "Ищем…"
              : results.length
                ? "Найденные группы"
                : "Группа не найдена"}
          </h3>
          {results.length > 0 ? (
            <GroupList results={results} opening={opening} onOpen={open} />
          ) : (
            !loading && (
              <div className="empty-state compact">
                <p>
                  Группы «{query.trim()}» нет в каталоге {data?.year || "университета"}.
                  Проверьте номер или импортируйте свой Word.
                </p>
                <button className="secondary" onClick={onImport}>
                  <Icon name="upload_file" size={20} />
                  Импорт из Word
                </button>
              </div>
            )
          )}
        </div>
      ) : (
        <>
          {current && (
            <div className="current-group">
              <div className="current-head">
                <span className="menu-icon">
                  <Icon name="groups" fill />
                </span>
                <div>
                  <small>Сейчас открыта</small>
                  <strong>{current.group}</strong>
                  <span>{describe(current) || current.name}</span>
                </div>
              </div>
              {subgroups.length > 0 && (
                <div className="chip-row">
                  <span>Подгруппа</span>
                  {[null, ...subgroups].map((label) => {
                    const selected = (subgroup || null) === label;
                    return (
                      <button
                        key={label || "all"}
                        className={`filter-chip ${selected ? "selected" : ""}`}
                        aria-pressed={selected}
                        aria-label={label || "Все подгруппы"}
                        onClick={() => onSubgroup(label)}
                      >
                        {selected && <Icon name="check" size={18} />}
                        {label ? label.replace("Подгруппа ", "") : "Все"}
                      </button>
                    );
                  })}
                </div>
              )}
              {siblings.length > 0 && (
                <div className="chip-row">
                  <span>В этом же документе</span>
                  {siblings.map((g) => (
                    <button key={g} className="filter-chip" onClick={() => onSibling(g)}>
                      {g}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {others.length > 0 && (
            <div className="result-group">
              <h3>Недавние</h3>
              <div className="recent-chips">
                {others.map((r) => (
                  <button key={`${r.document}|${r.group}`} onClick={() => onRecent(r)}>
                    <b>{r.group}</b>
                    <small>{describe(r) || "Импортированный Word"}</small>
                  </button>
                ))}
              </div>
            </div>
          )}

          {online && data && (
            <div className="result-group catalog-browse">
              <h3>Каталог {data.year}</h3>
              <div className="chip-row" role="group" aria-label="Факультет">
                {data.facets.faculties.map((f) => (
                  <button
                    key={f.name}
                    className={`filter-chip ${faculty === f.name ? "selected" : ""}`}
                    aria-pressed={faculty === f.name}
                    title={f.name}
                    onClick={() => {
                      setFaculty(faculty === f.name ? "" : f.name);
                      setCourse("");
                      setForm("");
                    }}
                  >
                    {faculty === f.name && <Icon name="check" size={18} />}
                    {facultyShort(f.name)}
                  </button>
                ))}
              </div>
              {faculty && (
                <>
                  <div className="chip-row" role="group" aria-label="Курс">
                    {data.facets.courses.map((c) => (
                      <button
                        key={c.name}
                        className={`filter-chip ${course === c.name ? "selected" : ""}`}
                        aria-pressed={course === c.name}
                        onClick={() => setCourse(course === c.name ? "" : c.name)}
                      >
                        {course === c.name && <Icon name="check" size={18} />}
                        {c.name}
                      </button>
                    ))}
                  </div>
                  {data.facets.forms.length > 1 && (
                    <div className="chip-row" role="group" aria-label="Форма обучения">
                      {data.facets.forms.map((f) => (
                        <button
                          key={f.name}
                          className={`filter-chip ${form === f.name ? "selected" : ""}`}
                          aria-pressed={form === f.name}
                          onClick={() => setForm(form === f.name ? "" : f.name)}
                        >
                          {form === f.name && <Icon name="check" size={18} />}
                          {formShort(f.name)}
                        </button>
                      ))}
                    </div>
                  )}
                  {results.length > 0 ? (
                    <GroupList results={results} opening={opening} onOpen={open} />
                  ) : (
                    !loading && <p className="coverage-note">Групп с такими условиями нет.</p>
                  )}
                </>
              )}
              {!faculty && (
                <p className="coverage-note">
                  Выберите факультет, чтобы посмотреть его группы, или введите номер
                  группы в поиске. Всего в каталоге {data.total}{" "}
                  {data.total % 10 === 1 && data.total % 100 !== 11 ? "группа" : "групп"}.
                </p>
              )}
            </div>
          )}
          {online && !data && loading && (
            <div className="schedule-skeleton" role="status" aria-label="Загружаем каталог">
              <span />
            </div>
          )}

          <div className="groups-footer">
            <span>Вашей группы нет в каталоге?</span>
            <button className="text-link" onClick={onImport}>
              <Icon name="upload_file" size={20} />
              Импортировать Word
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function GroupList({
  results,
  opening,
  onOpen,
}: {
  results: GroupResult[];
  opening: string;
  onOpen: (r: GroupResult) => void;
}) {
  return (
    <div className="result-list">
      {results.map((r) => {
        const key = `${r.fileId}|${r.group}`;
        return (
          <button
            key={key}
            disabled={!!opening}
            aria-busy={opening === key}
            onClick={() => onOpen(r)}
          >
            <span className="menu-icon">
              <Icon name="groups" />
            </span>
            <span>
              <b>{r.group}</b>
              <small>{describe(r)}</small>
            </span>
            {opening === key ? (
              <Icon name="refresh" className="spin" />
            ) : (
              <Icon name="chevron_right" />
            )}
          </button>
        );
      })}
    </div>
  );
}
